import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Dialog } from '@/components-new/Dialog/Dialog';
import { DialogButtons } from '@/components-new/Dialog/styles';
import { DetailsWidget } from '@/components/DetailsWidget/DetailsWidget';
import { ButtonNew } from '@/components-new/ButtonNew/ButtonNew';
import { Box } from '@/components/Box/Box';
import { Typography } from '@/components/Typography/Typography';
import { StatusWrapper } from '@/components/StatusWrapper/StatusWrapper';
import { STATUS_VARIANT_HIGHLIGHT } from '@/components/StatusWrapper/const';
import { showSnackbar } from '@/components/Snackbar/Snackbar';
import { copyToClipboard, showSnackbarResponseError } from '@/helpers/helpers';
import { formatDate, FULL_MONTH_DATE_FORMAT } from '@/helpers/dateHelpers/dateHelpers';
import { amplitudeService } from '@/services/amplitudeService/amplitudeService';
import { AMPLITUDE_EVENTS } from '@/services/amplitudeService/amplitudeEvents';
import { RecipientRequestStageActionType } from '@/api/v1/organization/vendor-vault/recipientRequestStageApi';
import { WireConfirmationPayloadType } from '@/api/v1/organization/vendor-vault/sendWireConfirmationApi';
import { KeyValueRow } from '@/bundle/_OrgAdmin/pages/MyVaultPage/styles';
import { loadRecipientRequest, runRequestStage, sendWireConfirmation, VENDOR_VAULT_QUERY_KEYS } from '../../api/api';
import { WireConfirmationForm } from './WireConfirmationForm';
import { HighlightPanel } from '@/components-new/HighlightPanel/styles';
import { downloadRecipientProfilePDF } from '@/bundle/pages/RecipientRequestPage/api/api';
import { saveAs } from 'file-saver';
import { getFileNameFromHeader } from '@/helpers/fileHelpers';
import { Mono } from '@/styles/common';

type RecipientProfileModalType = {
  isOpen: boolean;
  requestId: string;
  onClose: () => void;
  onChanged: () => void;
  onOpenVerification: () => void;
};

export const RecipientProfileModal = ({
  isOpen,
  requestId,
  onClose,
  onChanged,
  onOpenVerification,
}: RecipientProfileModalType) => {
  const queryClient = useQueryClient();
  const [reveal, setReveal] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const { data, isPending } = useQuery({
    queryKey: [VENDOR_VAULT_QUERY_KEYS.request, requestId, reveal],
    queryFn: () => loadRecipientRequest(requestId, reveal),
  });
  const request = data?.body;
  const profile = request?.profile;

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: [VENDOR_VAULT_QUERY_KEYS.request, requestId] });
    onChanged();
  };

  const stage = useMutation({
    mutationKey: ['recipient_request_stage', requestId],
    mutationFn: (action: RecipientRequestStageActionType) => runRequestStage(requestId, action),
    onSuccess(response, action) {
      if (response?.error) return showSnackbarResponseError(response.error);
      amplitudeService.logEvent(
        action === 'close' ? AMPLITUDE_EVENTS.VendorVaultClosed : AMPLITUDE_EVENTS.VendorVaultCreatedInBank
      );
      showSnackbar(
        action === 'close'
          ? `${response.body.recipient_name} closed and logged.`
          : `Marked as created in ${response.body.template_label}.`
      );
      refresh();
      if (action === 'close') onClose();
    },
  });

  const confirmation = useMutation({
    mutationKey: ['recipient_request_confirmation', requestId],
    mutationFn: (payload: WireConfirmationPayloadType) => sendWireConfirmation(requestId, payload),
    onSuccess(response) {
      if (response?.error) return showSnackbarResponseError(response.error);
      amplitudeService.logEvent(AMPLITUDE_EVENTS.VendorVaultConfirmationSent);
      showSnackbar(`Wire confirmation sent to ${response.body.recipient_name}.`);
      setConfirmOpen(false);
      refresh();
    },
  });

  const { mutate: downloadPDFMutate, isPending: isDownloadPDFPending } = useMutation({
    mutationKey: ['download_recipient_profile_pdf'],
    mutationFn: () => {
      return downloadRecipientProfilePDF(requestId);
    },
    onSuccess(response) {
      if (response?.error) {
        return showSnackbarResponseError(response?.error);
      }

      const header = response.headers.contentDisposition;
      const fileName = getFileNameFromHeader(header, `Recipient_profile_${requestId}.pdf`);

      saveAs(response?.body, fileName);
    },
  });

  const downloadPDF = () => {
    downloadPDFMutate();
  };

  const copyAll = () => {
    if (!profile) return;

    const copyDate = profile.rows.map((row) => `${row.label}: ${row.value}`).join('\n');

    copyToClipboard(copyDate);
  };

  return (
    <Dialog
      isOpen={isOpen}
      header={request ? `${request.recipient_name} · ${request.template_label}` : 'Recipient profile'}
      width='820px'
      onClose={onClose}
    >
      {request && profile && (
        <Box display='flex' gap='20px' flexDirection='column'>
          <Box display='flex' gap={'14px'} flexDirection='column' alignItems='flex-start'>
            <Box display='flex' alignItems='center' columnGap='8px' flexWrap='wrap'>
              <StatusWrapper
                variant={request.mode === 'verify' ? STATUS_VARIANT_HIGHLIGHT.GREEN : STATUS_VARIANT_HIGHLIGHT.YELLOW}
              >
                {request.mode === 'verify' ? 'Identity verified: ID + liveness' : 'Not verified (Request mode)'}
              </StatusWrapper>
              <StatusWrapper variant={STATUS_VARIANT_HIGHLIGHT.GREY}>
                Submitted {formatDate(profile.submitted_at, FULL_MONTH_DATE_FORMAT)}
              </StatusWrapper>
              {request.stage_label && (
                <StatusWrapper
                  variant={
                    request.stage === 'confirmed' ? STATUS_VARIANT_HIGHLIGHT.GREEN : STATUS_VARIANT_HIGHLIGHT.BLUE
                  }
                >
                  {request.stage_label}
                </StatusWrapper>
              )}
            </Box>
            {request.has_verification && (
              <ButtonNew variant='secondary' onClick={onOpenVerification}>
                Open ID verification record
              </ButtonNew>
            )}

            <Typography variant='bodySmall'>
              Fields are in the order {request.template_label} asks for them when you add a beneficiary.
            </Typography>
          </Box>

          <DetailsWidget isLoading={isPending} variant='small'>
            {request && profile && (
              <Box display='flex' flexDirection='column' rowGap='14px'>
                <Box>
                  {profile.rows.map((row) => (
                    <KeyValueRow key={row.key} style={{ gridTemplateColumns: '1fr 1.4fr auto' }}>
                      <span>{row.label}</span>
                      {row.sensitive ? <Mono>{row.value || '-'}</Mono> : <span>{row.value || '-'}</span>}
                      <ButtonNew
                        variant='secondary'
                        disabled={!reveal && row.sensitive}
                        onClick={() => copyToClipboard(row.value)}
                      >
                        Copy
                      </ButtonNew>
                    </KeyValueRow>
                  ))}
                </Box>
              </Box>
            )}
          </DetailsWidget>

          <Box
            display='flex'
            justifyContent='space-between'
            alignItems='flex-start'
            gap='10px'
            flexWrap='wrap'
            flexDirection='column'
          >
            <Box display='flex' columnGap='10px'>
              <ButtonNew size='medium' variant='secondary' onClick={() => setReveal((value) => !value)}>
                {reveal ? 'Hide numbers' : 'Reveal numbers'}
              </ButtonNew>
              <ButtonNew size='medium' variant='secondary' disabled={!reveal} onClick={copyAll}>
                Copy all fields
              </ButtonNew>
              <ButtonNew
                size='medium'
                variant='secondary'
                disabled={!reveal || isDownloadPDFPending}
                onClick={downloadPDF}
              >
                Download PDF
              </ButtonNew>
            </Box>
            <Typography variant='bodySmall'>
              Wire confirmation requested:{' '}
              <b>{request.wire_confirmation ? `Yes, ${profile.confirmation_channel || 'email'}` : 'No'}</b>
              <br />
              {profile.blockchain_id && (
                <>
                  Record <Mono>{profile.record_hash.slice(0, 8)}</Mono> on chain
                </>
              )}
            </Typography>
          </Box>

          {request.confirmation_reference && (
            <Typography variant='bodySmall'>
              Confirmation(or tracking number) {request.confirmation_reference}
              {request.confirmation_amount ? ` · amount ${request.confirmation_amount}` : ''}
              {request.confirmation_date ? ` · sent ${formatDate(request.confirmation_date)}` : ''}
            </Typography>
          )}

          <HighlightPanel>
            <Typography variant='bodySmall' fontWeight='semibold'>
              Next actions
            </Typography>
            <Box display='flex' columnGap='10px' rowGap='10px' flexWrap='wrap' marginTop={'12px'}>
              {request.stage === 'submitted' && (
                <ButtonNew size='medium' isLoading={stage.isPending} onClick={() => stage.mutate('created-in-bank')}>
                  Recipient created in {request.template_label}
                </ButtonNew>
              )}
              {request.stage === 'created' && request.wire_confirmation && (
                <ButtonNew size='medium' onClick={() => setConfirmOpen((value) => !value)}>
                  Funds sent: send wire confirmation
                </ButtonNew>
              )}
              {request.stage !== 'submitted' && request.status !== 'closed' && (
                <ButtonNew
                  size='medium'
                  variant='secondary'
                  isLoading={stage.isPending}
                  onClick={() => stage.mutate('close')}
                >
                  Close and move to audit log
                </ButtonNew>
              )}
            </Box>
            {confirmOpen && request.stage === 'created' && (
              <Box marginTop='12px'>
                <WireConfirmationForm
                  defaultSendTo={profile.confirmation_contact || request.sent_to}
                  isLoading={confirmation.isPending}
                  onSubmit={(payload) => confirmation.mutate(payload)}
                />
              </Box>
            )}
          </HighlightPanel>
        </Box>
      )}

      <DialogButtons>
        <ButtonNew size='medium' variant='secondary' onClick={onClose}>
          Close
        </ButtonNew>
      </DialogButtons>
    </Dialog>
  );
};
